import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, extractErr } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Download, Copy, Film, Trash2, FileText, Cloud, CheckCircle2, AlertCircle, ChevronDown, ChevronRight, ExternalLink } from "lucide-react";
import { useT } from "@/i18n";
import { openCvForPrint, renderCvMarkdownToHtml } from "@/lib/cvPrint";
import { previewCss, DEFAULT_TEMPLATE_ID, getTemplate } from "@/lib/cvTemplates";
import CvTemplatePicker from "@/components/CvTemplatePicker";

export default function ProjectDetail() {
  const { id } = useParams();
  const { t } = useT();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [templateId, setTemplateId] = useState(DEFAULT_TEMPLATE_ID);

  // Load saved template preference per CV
  useEffect(() => {
    const saved = localStorage.getItem(`cv-template-${id}`);
    if (saved && getTemplate(saved).id === saved) setTemplateId(saved);
  }, [id]);

  const handleTemplateChange = (tid) => {
    setTemplateId(tid);
    localStorage.setItem(`cv-template-${id}`, tid);
  };

  useEffect(() => {
    api.get(`/projects/${id}`)
      .then((r) => setProject(r.data))
      .catch(() => toast.error(t("err_generic")))
      .finally(() => setLoading(false));
  }, [id, t]);

  // Live-refresh Salesforce projects while the background task runs. Stops as
  // soon as status flips to "done" or "error" so we don't poll forever.
  useEffect(() => {
    if (!project || project.kind !== "salesforce") return;
    const status = project.content?.status;
    if (status !== "running") return;
    const tick = setInterval(() => {
      api.get(`/projects/${id}`)
        .then((r) => setProject(r.data))
        .catch(() => {});
    }, 2500);
    return () => clearInterval(tick);
  }, [project, id]);

  const downloadMp4 = () => {
    if (!project?.video_url) return;
    const link = document.createElement("a");
    link.href = `${process.env.REACT_APP_BACKEND_URL}${project.video_url}`;
    link.download = `lumioo-${project.id}.mp4`;
    link.target = "_blank";
    link.click();
  };

  const downloadAudio = () => {
    if (!project?.voiceover_base64) return;
    const link = document.createElement("a");
    link.href = `data:audio/mp3;base64,${project.voiceover_base64}`;
    link.download = `lumioo-voiceover-${project.id}.mp3`;
    link.click();
  };

  const copyMeta = () => {
    const text = `Title: ${project.title}\n\nDescription:\n${project.description}\n\nHashtags: ${(project.hashtags || []).join(" ")}`;
    navigator.clipboard.writeText(text);
    toast.success(t("video_meta_copied"));
  };

  const downloadCV = () => {
    const md = project?.content?.markdown;
    if (!md) { toast.error("CV content not found"); return; }
    const ok = openCvForPrint(md, project?.title || "CV", templateId);
    if (ok) toast.success("Opening CV for print/PDF…");
    else toast.error("Please allow pop-ups to download the CV");
  };

  const copyCvMarkdown = () => {
    const md = project?.content?.markdown;
    if (!md) return;
    navigator.clipboard.writeText(md);
    toast.success("CV markdown copied");
  };

  const handleDelete = async () => {
    if (!window.confirm(t("project_confirm_delete"))) return;
    try {
      await api.delete(`/projects/${id}`);
      toast.success(t("project_deleted"));
      navigate("/app/projects");
    } catch {
      toast.error(t("err_generic"));
    }
  };

  if (loading) {
    return <div className="p-10 flex items-center justify-center text-zinc-500"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }
  if (!project) {
    return <div className="p-10 text-center text-zinc-500">{t("project_not_found")}</div>;
  }

  const isCv = project.kind === "cv";
  const isSf = project.kind === "salesforce";

  return (
    <div data-testid="project-detail-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <Link to="/app/projects" className="inline-flex items-center gap-1.5 text-sm font-semibold text-zinc-700 hover:text-blue-600" data-testid="project-back-btn">
          <ArrowLeft className="w-4 h-4" /> {t("project_back")}
        </Link>
        <button onClick={handleDelete} data-testid="project-delete-btn"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-zinc-300 text-sm font-semibold text-zinc-700 hover:bg-red-50 hover:border-red-600 hover:text-red-600 rounded-md">
          <Trash2 className="w-4 h-4" /> {t("project_delete")}
        </button>
      </div>

      <div className="mt-4 text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
        {isCv ? "CV · " : isSf ? "SALESFORCE · " : project.language ? `${project.language} · ` : ""}{new Date(project.created_at).toLocaleString()}
      </div>
      <h1 className="mt-2 text-3xl sm:text-4xl font-black tracking-tighter" data-testid="project-title">{project.title}</h1>

      {/* Salesforce project — show all generated artifacts (Apex, LWC, Aura, Flows…) */}
      {isSf && <SalesforceProjectView content={project.content || {}} projectId={project.id} />}

      {/* CV preview */}
      {isCv && project?.content?.markdown && (
        <div className="mt-8 border border-zinc-200 bg-white" data-testid="project-cv-block">
          <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between flex-wrap gap-3 bg-zinc-50">
            <div className="text-base font-semibold flex items-center gap-2">
              <FileText className="w-4 h-4 text-orange-600" /> CV Preview
            </div>
            <div className="flex items-center gap-2">
              <button onClick={copyCvMarkdown} data-testid="project-cv-copy"
                className="inline-flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-md border border-zinc-300 hover:bg-zinc-100">
                <Copy className="w-3.5 h-3.5" /> Copy markdown
              </button>
              <button onClick={downloadCV} data-testid="project-cv-download"
                className="btn-accent inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-md">
                <Download className="w-4 h-4" /> Download PDF
              </button>
            </div>
          </div>
          <div className="px-6 py-3 border-b border-zinc-200 bg-white">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 mb-2">Template</div>
            <CvTemplatePicker value={templateId} onChange={handleTemplateChange} />
          </div>
          <div className="px-8 py-8">
            <div className="cv-preview"
              dangerouslySetInnerHTML={{ __html: renderCvMarkdownToHtml(project.content.markdown) }} />
          </div>
          <style>{previewCss(getTemplate(templateId))}</style>
        </div>
      )}

      {/* Video player */}
      {project.video_url ? (
        <div className="mt-8 border border-zinc-200 bg-white p-6" data-testid="project-video-block">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="text-base font-semibold flex items-center gap-2">
              <Film className="w-4 h-4 text-blue-600" /> MP4
            </div>
            <button onClick={downloadMp4} data-testid="project-download-mp4"
              className="btn-accent inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-md">
              <Download className="w-4 h-4" /> {t("video_download_video")}
            </button>
          </div>
          <video controls src={`${process.env.REACT_APP_BACKEND_URL}${project.video_url}`}
            className="w-full mt-4 rounded-md border border-zinc-200 bg-black" data-testid="project-video-player" />
        </div>
      ) : !isCv ? (
        <div className="mt-8 border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          {t("project_no_video")}
        </div>
      ) : null}

      {/* Meta (video projects only) */}
      {!isCv && (project.description || (project.hashtags || []).length > 0) && (
        <div className="mt-6 border border-zinc-200 bg-white p-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500">{t("video_desc_l")}</div>
            <button onClick={copyMeta} data-testid="project-copy-meta" className="btn-outline inline-flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-md">
              <Copy className="w-3.5 h-3.5" /> {t("video_copy_meta")}
            </button>
          </div>
          <p className="mt-2 text-sm text-zinc-700 whitespace-pre-wrap leading-relaxed">{project.description}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {(project.hashtags || []).map((h, i) => (
              <span key={i} className="px-2.5 py-1 text-xs font-mono bg-zinc-100 border border-zinc-200 rounded text-zinc-700">{h}</span>
            ))}
          </div>
        </div>
      )}

      {/* Voiceover */}
      {project.voiceover_base64 && (
        <div className="mt-6 border border-zinc-200 bg-white p-6" data-testid="project-voiceover-block">
          <div className="flex items-center justify-between">
            <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500">{t("video_voiceover")}</div>
            <button onClick={downloadAudio} data-testid="project-download-audio"
              className="btn-primary inline-flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-md">
              <Download className="w-3.5 h-3.5" /> MP3
            </button>
          </div>
          <audio controls src={`data:audio/mp3;base64,${project.voiceover_base64}`} className="w-full mt-3" />
        </div>
      )}

      {/* Scenes */}
      {project.scenes?.length > 0 && (
        <div className="mt-6">
          <h3 className="text-lg font-bold tracking-tight">{t("video_scenes")}</h3>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
            {project.scenes.map((s, i) => (
              <div key={i} className="border border-zinc-200 bg-white overflow-hidden" data-testid={`project-scene-${i}`}>
                <div className="aspect-video bg-zinc-100 relative">
                  {s.image_base64 ? (
                    <img src={`data:image/png;base64,${s.image_base64}`} alt={`${t("scene")} ${i + 1}`} className="w-full h-full object-cover" />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-zinc-400 text-xs">{t("image_unavailable")}</div>
                  )}
                  <div className="absolute top-2 left-2 px-2 py-0.5 bg-zinc-950 text-white text-[10px] font-mono uppercase tracking-wider">
                    {t("scene")} {i + 1}
                  </div>
                </div>
                <div className="p-4">
                  <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Narration</div>
                  <p className="mt-1 text-sm text-zinc-800 leading-relaxed">{s.narration}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Salesforce project — code review accordion ───────────────────────────
const SF_CODE_FIELDS = {
  apex_create_class: [["Apex", "body"]],
  apex_create_trigger: [["Trigger", "body"]],
  apex_execute: [["Apex (anonymous)", "body"]],
  apex_page_create: [["Visualforce", "markup"]],
  lwc_create: [["HTML", "html"], ["JavaScript", "js"], ["CSS", "css"], ["Meta XML", "meta_xml"]],
  aura_create: [["Component", "component_markup"], ["Controller", "controller_js"], ["Helper", "helper_js"], ["Style", "style_css"]],
  flow_create: [["Flow XML", "metadata_xml"]],
  query: [["SOQL", "soql"]],
  soql: [["SOQL", "soql"]],
  tooling_query: [["SOQL (Tooling)", "soql"]],
  validation_rule_create: [["Formula", "error_condition_formula"]],
  create: [["Fields", "fields"]],
  update: [["Fields", "fields"]],
};

function SfCodeAccordion({ tool, args }) {
  const [open, setOpen] = useState(false);
  const blocks = (SF_CODE_FIELDS[tool] || []).map(([label, key]) => {
    let v = args?.[key];
    if (v == null || v === "") return null;
    if (typeof v !== "string") v = JSON.stringify(v, null, 2);
    return { label, value: v };
  }).filter(Boolean);
  if (!blocks.length) return null;
  return (
    <div className="mt-2">
      <button type="button" onClick={() => setOpen((v) => !v)} className="inline-flex items-center gap-1 text-xs font-bold text-zinc-700 hover:text-zinc-900">
        {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        {open ? "Hide code" : "Show code"}
      </button>
      {open && (
        <div className="mt-2 space-y-2">
          {blocks.map((b, i) => (
            <div key={i} className="rounded border border-zinc-200 bg-zinc-50">
              <div className="flex items-center justify-between px-2 py-1 border-b border-zinc-200 bg-zinc-100">
                <span className="text-[10px] font-bold text-zinc-700 uppercase">{b.label}</span>
                <button type="button" onClick={() => { navigator.clipboard.writeText(b.value); toast.success(`${b.label} copied`); }} className="text-[10px] font-bold text-sky-700 hover:text-sky-900">Copy</button>
              </div>
              <pre className="text-[11px] leading-snug px-2 py-1.5 max-h-72 overflow-auto font-mono whitespace-pre-wrap break-all text-zinc-800">{b.value}</pre>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SalesforceProjectView({ content, projectId }) {
  const [steps, setSteps] = useState(content?.results || []);
  const [fixingIdx, setFixingIdx] = useState(null);
  // Keep local steps in sync when the parent re-fetches (live polling case).
  useEffect(() => {
    setSteps(content?.results || []);
  }, [content?.results]);
  const okCount = steps.filter((s) => s.ok).length;
  const running = content?.status === "running";
  const progressTotal = content?.progress?.total || (content?.plan?.steps?.length) || steps.length;

  const tryFix = async (stepIdx) => {
    setFixingIdx(stepIdx);
    try {
      // Fire-and-forget background fix. Poll the project doc until the step's
      // fix_status flips from "fixing" → "done" (or it succeeds / fails).
      await api.post("/integrations/salesforce/agent/fix-step", {
        plan_id: projectId,
        step_index: stepIdx,
      });
      toast.info("AI is reworking that step — keeps going in the background.");
      const startTs = Date.now();
      const poll = async () => {
        try {
          const { data } = await api.get(`/projects/${projectId}`);
          const updated = (data?.content?.results || [])[stepIdx];
          if (updated && updated.fix_status === "done") {
            setSteps(data.content.results);
            toast.success(updated.ok ? "Step fixed ✓" : "Couldn't fix — see new error");
            setFixingIdx(null);
            return;
          }
          if (Date.now() - startTs > 240000) { // 4-minute safety bail-out
            setFixingIdx(null);
            toast.error("Fix is taking too long — refresh the page to see the result");
            return;
          }
          setTimeout(poll, 2500);
        } catch (e) {
          setTimeout(poll, 4000);
        }
      };
      setTimeout(poll, 1500);
    } catch (e) {
      toast.error(extractErr(e, "Fix failed"));
      setFixingIdx(null);
    }
  };

  return (
    <div className="mt-6">
      <div className="rounded-xl border-2 p-4 text-white" style={{ borderColor: "#00A1E0", background: "linear-gradient(135deg,#001a33 0%,#003e6b 60%,#00A1E0 130%)" }}>
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em]">
            <Cloud className="w-3.5 h-3.5" />
            {running ? (
              <span className="inline-flex items-center gap-1.5">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                AI is working… {okCount}/{progressTotal} steps done
              </span>
            ) : (
              <>Salesforce Workspace · {okCount}/{steps.length} steps OK</>
            )}
          </div>
          {!running && (
            <Link
              to={`/app/salesforce?continue=${projectId}`}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-bold text-[#003e6b] bg-white hover:bg-zinc-100"
              data-testid="sf-continue-project"
            >
              Continue this project <ChevronRight className="w-3 h-3" />
            </Link>
          )}
        </div>
        {content?.prompt && (
          <div className="mt-2 text-sm italic text-sky-50/90">&ldquo;{content.prompt}&rdquo;</div>
        )}
        {content?.instance_url && (
          <a href={content.instance_url} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-[11px] font-bold text-sky-200 hover:text-white underline">
            {content.instance_url.replace(/^https?:\/\//, "")} <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>

      <ol className="mt-5 space-y-3" data-testid="sf-project-steps">
        {steps.map((s, i) => {
          const retries = Array.isArray(s.attempts) ? Math.max(0, s.attempts.length - 1) : 0;
          return (
            <li key={i} className={`rounded-md px-4 py-3 border ${s.ok ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"}`} data-testid={`sf-step-${i}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 text-sm">
                    <span className="font-mono font-bold text-zinc-800">{i + 1}. {s.tool}</span>
                    {s.ok ? (
                      <span className="text-emerald-700 inline-flex items-center gap-0.5 text-xs"><CheckCircle2 className="w-3.5 h-3.5" /> ok{retries > 0 ? ` · AI fixed in ${retries}` : ""}</span>
                    ) : (
                      <span className="text-red-700 inline-flex items-center gap-0.5 text-xs"><AlertCircle className="w-3.5 h-3.5" /> failed</span>
                    )}
                  </div>
                  {s.explain && <div className="mt-0.5 text-xs text-zinc-600 italic">{s.explain}</div>}
                  {s.summary && <div className="mt-0.5 text-xs text-zinc-700 break-all">→ {s.summary}</div>}
                  {!s.ok && s.error && <div className="mt-1 text-[11px] text-red-700 break-all font-mono">{String(s.error).slice(0, 400)}</div>}
                </div>
                <div className="shrink-0 flex flex-col items-end gap-1">
                  {s.url && (
                    <a href={s.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 text-xs font-bold text-sky-700 hover:text-sky-900 underline whitespace-nowrap">
                      Open <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                  {!s.ok && (
                    <button
                      onClick={() => tryFix(i)}
                      disabled={fixingIdx !== null}
                      data-testid={`pd-sf-fix-step-${i}`}
                      className="inline-flex items-center gap-0.5 text-[11px] font-bold text-white px-2 py-1 rounded disabled:opacity-50"
                      style={{ background: "#dc2626" }}
                      title="Ask the AI to retry this step with a smarter fix"
                    >
                      {fixingIdx === i ? <Loader2 className="w-3 h-3 animate-spin" /> : <>Fix it ↻</>}
                    </button>
                  )}
                </div>
              </div>
              <SfCodeAccordion tool={s.tool} args={s.args} />
            </li>
          );
        })}
      </ol>

      {/* Auto-fix status banner */}
      {content?.auto_fix_status && content.auto_fix_status !== "done" && content.auto_fix_status !== "no_results" && (
        <div className="mt-4 rounded-lg border-2 p-3 flex items-center gap-2" style={{ borderColor: "#f59e0b", background: "#fffbeb" }} data-testid="pd-auto-fix-status">
          {(content.auto_fix_status === "planning" || content.auto_fix_status === "executing") ? (
            <Loader2 className="w-4 h-4 animate-spin text-amber-600" />
          ) : (
            <AlertCircle className="w-4 h-4 text-amber-600" />
          )}
          <div className="text-xs text-amber-900 flex-1">
            {content.auto_fix_status === "planning" && <span>🔧 <b>Auto-fix devrede</b> — AI <b>{content.auto_fix_failed_count}</b> başarısız step için düzeltici plan üretiyor…</span>}
            {content.auto_fix_status === "executing" && <span>🔧 <b>Auto-fix uygulanıyor</b> — <b>{content.auto_fix_step_count}</b> corrective step Salesforce'a deploy ediliyor…</span>}
            {content.auto_fix_status === "no_plan" && <span>⚠ Auto-fix corrective step üretemedi: {content.auto_fix_error || "AI çözüm bulamadı, manuel Fix it butonlarını kullan"}</span>}
            {content.auto_fix_status === "failed" && <span>⚠ Auto-fix crashed: {content.auto_fix_error}</span>}
          </div>
        </div>
      )}
      {content?.auto_fix_status === "done" && content.auto_fix_total > 0 && (
        <div className="mt-4 rounded-lg border-2 p-3 flex items-center gap-2" style={{ borderColor: "#10b981", background: "#ecfdf5" }} data-testid="pd-auto-fix-done">
          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
          <div className="text-xs text-emerald-900">
            ✅ <b>Auto-fix tamamlandı</b> · {content.auto_fix_ok_count}/{content.auto_fix_total} corrective step başarılı (attempt {content.auto_fix_attempt})
          </div>
        </div>
      )}

      <SfBuildGallery
        projectId={projectId}
        steps={steps}
        galleryStatus={content?.gallery_status}
        galleryReason={content?.gallery_reason}
        running={running}
      />
    </div>
  );
}

// Tiny component that fetches a screenshot via the authenticated /agent/screenshot
// endpoint and renders it as an <img>. Uses an in-memory Blob URL so the Bearer
// token stays in the request header (never leaks into URL or browser history).
function SfGalleryImage({ fileId, alt }) {
  const [src, setSrc] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let revoke = null;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/integrations/salesforce/agent/screenshot/${fileId}`, { responseType: "blob" });
        if (cancelled) return;
        const url = URL.createObjectURL(res.data);
        revoke = url;
        setSrc(url);
      } catch (_) {
        if (!cancelled) setErr(true);
      }
    })();
    return () => {
      cancelled = true;
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [fileId]);
  if (err) return <div className="aspect-video bg-zinc-100 rounded flex items-center justify-center text-xs text-zinc-500">image unavailable</div>;
  if (!src) return <div className="aspect-video bg-zinc-100 rounded animate-pulse" />;
  return <img src={src} alt={alt} className="w-full aspect-video object-cover object-top rounded" loading="lazy" />;
}

function SfBuildGallery({ projectId, steps, galleryStatus, galleryReason, running }) {
  const [recapturing, setRecapturing] = useState(false);
  const shots = (steps || []).filter((s) => s.ok && s.screenshot_file_id);
  if (running) return null;

  const capturing = galleryStatus === "capturing";
  const isError = ["all_failed", "failed", "skipped_no_token", "skipped_no_chromium"].includes(galleryStatus);
  const isEmpty = ["no_targets", "skipped_no_steps"].includes(galleryStatus);
  const hasShots = shots.length > 0;

  // Only completely hide if: not capturing, no shots, no error, and no terminal "empty" status set.
  // This makes the section visible whenever there's something for the user to know about.
  if (!capturing && !hasShots && !isError && !isEmpty) return null;

  const recapture = async () => {
    setRecapturing(true);
    try {
      await api.post("/integrations/salesforce/agent/screenshot/recapture", { plan_id: projectId });
      toast.info("Re-capturing gallery in the background — refresh in ~30s");
    } catch (e) {
      toast.error(extractErr(e, "Re-capture failed"));
    } finally {
      setRecapturing(false);
    }
  };

  // Headline & action affordance per status
  let headline;
  if (capturing) headline = "Capturing screenshots from your Salesforce org…";
  else if (hasShots) headline = `${shots.length} screenshot${shots.length === 1 ? "" : "s"} of what the AI built`;
  else if (galleryStatus === "no_targets") headline = "No URL-bearing steps to screenshot";
  else if (galleryStatus === "skipped_no_steps") headline = "No successful steps — nothing to screenshot yet";
  else if (galleryStatus === "skipped_no_token") headline = "⚠ Salesforce session expired — reconnect to enable screenshots";
  else if (galleryStatus === "skipped_no_chromium") headline = "⚠ Screenshot engine unavailable on this server";
  else if (galleryStatus === "all_failed") headline = "⚠ All screenshots failed — likely an expired Salesforce session";
  else if (galleryStatus === "failed") headline = "⚠ Screenshot capture crashed";
  else headline = "No screenshots yet";

  return (
    <div className="mt-8 rounded-xl border-2 p-5 bg-white" style={{ borderColor: isError ? "#dc2626" : "#00A1E0" }} data-testid="sf-build-gallery">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <span className={`text-[10px] font-black uppercase tracking-[0.2em] px-2 py-0.5 rounded-full text-white whitespace-nowrap`} style={{ background: isError ? "linear-gradient(135deg,#dc2626,#7f1d1d)" : "linear-gradient(135deg,#00A1E0,#003e6b)" }}>
            📸 Build Gallery
          </span>
          <span className="text-xs text-zinc-700 truncate">{headline}</span>
        </div>
        {!capturing && (
          <button
            onClick={recapture}
            disabled={recapturing}
            data-testid="sf-gallery-recapture"
            className="text-[11px] font-bold px-2.5 py-1 rounded border border-sky-200 text-sky-700 hover:bg-sky-50 disabled:opacity-50 whitespace-nowrap"
            title="Re-run the screenshot capture (useful after reconnecting Salesforce)"
          >
            {recapturing ? <Loader2 className="w-3 h-3 animate-spin inline" /> : "Re-capture ↻"}
          </button>
        )}
      </div>

      {(isError || isEmpty) && galleryReason && (
        <div className={`mt-3 text-[11px] rounded px-3 py-2 ${isError ? "bg-red-50 text-red-800 border border-red-200" : "bg-zinc-50 text-zinc-600 border border-zinc-200"}`}>
          {galleryReason}
          {galleryStatus === "skipped_no_token" && (
            <Link to="/app/integrations" className="ml-2 underline font-bold">Reconnect Salesforce →</Link>
          )}
        </div>
      )}

      {capturing && shots.length === 0 && (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          {[0, 1, 2].map((k) => (
            <div key={k} className="aspect-video bg-zinc-100 rounded animate-pulse" />
          ))}
        </div>
      )}

      {shots.length > 0 && (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="sf-gallery-grid">
          {shots.map((s) => (
            <div key={s.screenshot_file_id} className="border rounded-lg overflow-hidden hover:shadow-md transition-shadow bg-white">
              <SfGalleryImage fileId={s.screenshot_file_id} alt={s.tool} />
              <div className="p-2.5 border-t bg-zinc-50">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-[11px] font-bold text-zinc-800 truncate">{s.tool}</span>
                  {s.url && (
                    <a href={s.url} target="_blank" rel="noreferrer" className="text-[10px] font-bold text-sky-700 hover:text-sky-900 inline-flex items-center gap-0.5 shrink-0">
                      Open <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
                {s.explain && <div className="mt-0.5 text-[10px] text-zinc-500 italic line-clamp-2">{s.explain}</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
